import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Card, CardContent } from "./components/ui/card";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { 
  Menu, 
  X, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  ChevronRight,
  Calendar,
  Heart,
  Sparkles,
  Home as HomeIcon,
  ArrowLeft,
  Shield
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_61bbdadf-b0ff-4916-8107-ec530456a26e/artifacts/cugk288f_WhatsApp%20Image%202026-01-27%20at%2010.46.30.jpeg",
  heroImage: "https://customer-assets.emergentagent.com/job_61bbdadf-b0ff-4916-8107-ec530456a26e/artifacts/rx0vckh0_IMG_1769.JPG",
  cabinetImage: "https://customer-assets.emergentagent.com/job_61bbdadf-b0ff-4916-8107-ec530456a26e/artifacts/1nfcsm8r_PXL_20260131_143724516.jpg",
  chiroImage: "https://customer-assets.emergentagent.com/job_61bbdadf-b0ff-4916-8107-ec530456a26e/artifacts/740p4bfc_IMG_1761.JPG",
  massageImage: "https://customer-assets.emergentagent.com/job_61bbdadf-b0ff-4916-8107-ec530456a26e/artifacts/k0wzagu8_IMG_2011.JPG"
};

// Contact info
const CONTACT = {
  phone: "0756926624",
  email: "marie.millot75@gmail.com",
  address: "27 rue Colmet Lepinay, 93100 Montreuil",
  addressNote: "Cabinet de santé (et non l'immeuble voisin)",
  doctolib: "https://www.doctolib.fr/chiropracteur/montreuil/marie-millot-montreuil",
  calendly: "https://calendly.com/marie-millot75"
};

// Navigation Component
const Navigation = ({ isOpen, setIsOpen }) => {
  const navItems = [
    { name: "Accueil", href: "#accueil" },
    { name: "Services", href: "#services" },
    { name: "Tarifs", href: "#tarifs" },
    { name: "À propos", href: "#apropos" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-glass border-b border-[#E6F2F2]" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <a href="#accueil" className="flex items-center gap-4" data-testid="nav-logo">
            <img 
              src={ASSETS.logo} 
              alt="Le corps en main" 
              className="h-20 w-auto"
            />
            <span className="text-3xl font-medium text-[#0F5156] hidden sm:block" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Le corps en main
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-[#0F5156] hover:text-[#0F5156]/70 transition-colors font-medium"
                data-testid={`nav-link-${item.name.toLowerCase()}`}
              >
                {item.name}
              </a>
            ))}
            <a 
              href={CONTACT.doctolib}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-rdv-button"
            >
              <Button className="bg-[#0F5156] hover:bg-[#0d4246] text-white rounded-full px-6">
                <Calendar className="w-4 h-4 mr-2" />
                Prendre RDV
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#0F5156]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-[#E6F2F2]" data-testid="mobile-menu">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="block py-3 text-[#0F5156] hover:text-[#0F5156]/70 transition-colors font-medium"
                onClick={() => setIsOpen(false)}
                data-testid={`mobile-nav-link-${item.name.toLowerCase()}`}
              >
                {item.name}
              </a>
            ))}
            <a 
              href={CONTACT.doctolib}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4"
              data-testid="mobile-rdv-button"
            >
              <Button className="w-full bg-[#0F5156] hover:bg-[#0d4246] text-white rounded-full">
                <Calendar className="w-4 h-4 mr-2" />
                Prendre RDV
              </Button>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="accueil" className="hero-bg min-h-screen pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="animate-fade-in-up">
            <p className="text-sm tracking-widest uppercase text-[#0F5156]/60 font-semibold mb-4">
              Chiropraxie & Massage à Montreuil
            </p>
            <h1 className="text-5xl md:text-7xl font-light tracking-tight leading-[1.1] text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Le corps<br />en main
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#0F5156]/70 mb-8 max-w-lg">
              Marie Millot vous accompagne avec douceur et bienveillance vers un mieux-être physique et mental, au cabinet ou à domicile.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={CONTACT.doctolib}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-rdv-chiro"
              >
                <Button className="bg-[#0F5156] hover:bg-[#0d4246] text-white rounded-full px-8 py-6 text-lg w-full sm:w-auto">
                  <Calendar className="w-5 h-5 mr-2" />
                  RDV Chiropraxie
                </Button>
              </a>
              <a href="#services" data-testid="hero-discover-services">
                <Button variant="outline" className="border-[#0F5156] text-[#0F5156] hover:bg-[#E6F2F2] rounded-full px-8 py-6 text-lg w-full sm:w-auto">
                  Découvrir les soins
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>

          {/* Right Image */}
          <div className="animate-fade-in-up animation-delay-200">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#E6F2F2] rounded-[3rem] transform rotate-3"></div>
              <img
                src={ASSETS.heroImage}
                alt="Marie Millot - Chiropracteure"
                className="relative rounded-[2.5rem] w-full h-[500px] lg:h-[600px] object-cover shadow-[0_20px_60px_rgba(15,81,86,0.2)]"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      title: "Chiropraxie",
      location: "Au cabinet",
      description: "Des soins chiropratiques pour soulager vos douleurs et améliorer votre mobilité. Une approche holistique pour traiter la cause et non les symptômes.",
      prices: ["Première visite (1h) : 70€", "Suivi (45min) : 60€"],
      image: ASSETS.chiroImage,
      icon: <Sparkles className="w-6 h-6" />,
      link: CONTACT.doctolib,
      linkText: "Réserver sur Doctolib",
      testId: "service-chiro"
    },
    {
      title: "Massages Bien-être",
      location: "À domicile",
      description: "Massages californiens relaxants ou suédois plus toniques, directement chez vous. Offrez-vous un moment de détente et de retour à soi.",
      prices: ["1h : 80€", "1h15 : 95€", "1h30 : 110€"],
      image: ASSETS.massageImage,
      icon: <Heart className="w-6 h-6" />,
      link: "#contact",
      linkText: "Me contacter",
      contactNote: "Pour prendre rendez-vous pour un massage, contactez-moi par SMS ou par mail.",
      testId: "service-massage"
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-sm tracking-widest uppercase text-[#0F5156]/60 font-semibold mb-4">
            Mes soins
          </p>
          <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Services proposés
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {services.map((service) => (
            <Card 
              key={service.title} 
              className="service-card border-0 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl overflow-hidden bg-white"
              data-testid={service.testId}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
                  <HomeIcon className="w-4 h-4 text-[#0F5156]" />
                  <span className="text-sm font-medium text-[#0F5156]">{service.location}</span>
                </div>
              </div>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#E6F2F2] rounded-full flex items-center justify-center text-[#0F5156]">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-medium text-[#0F5156]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {service.title}
                  </h3>
                </div>
                <p className="text-[#0F5156]/70 leading-relaxed mb-6">
                  {service.description}
                </p>
                <div className="space-y-2 mb-6">
                  {service.prices.map((price, idx) => (
                    <p key={idx} className="text-[#0F5156] font-medium">{price}</p>
                  ))}
                  {service.contactNote && (
                    <p className="text-[#0F5156]/70 text-sm italic mt-4">{service.contactNote}</p>
                  )}
                </div>
                <a 
                  href={service.link}
                  target={service.link.startsWith('http') ? "_blank" : undefined}
                  rel={service.link.startsWith('http') ? "noopener noreferrer" : undefined}
                  data-testid={`${service.testId}-button`}
                >
                  <Button className="w-full bg-[#0F5156] hover:bg-[#0d4246] text-white rounded-full py-6">
                    {service.linkText}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quote Section
const QuoteSection = () => {
  return (
    <section className="quote-section py-24 md:py-32" data-testid="quote-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <p className="text-3xl md:text-4xl lg:text-5xl font-light italic text-white leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          "Si vous écoutez votre corps lorsqu'il chuchote, vous n'aurez pas à l'entendre crier"
        </p>
        <p className="mt-8 text-white/80 text-lg tracking-widest uppercase">
          — Proverbe Tibétain
        </p>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const pricing = {
    chiropraxie: [
      { service: "Première consultation", duration: "1h", price: "70€" },
      { service: "Consultation de suivi", duration: "45min", price: "60€" }
    ],
    massages: [
      { service: "Massage Californien ou Suédois", duration: "1h", price: "80€" },
      { service: "Massage Californien ou Suédois", duration: "1h15", price: "95€" },
      { service: "Massage Californien ou Suédois", duration: "1h30", price: "110€" }
    ]
  };

  return (
    <section id="tarifs" className="py-24 md:py-32 bg-[#F8FCFC]" data-testid="pricing-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Tarifs
          </h2>
        </div>

        {/* Chiropraxie */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-6 h-6 text-[#0F5156]" />
            <h3 className="text-2xl font-medium text-[#0F5156]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Chiropraxie (au cabinet)
            </h3>
          </div>
          <div className="space-y-4">
            {pricing.chiropraxie.map((item, idx) => (
              <div key={idx} className="flex items-baseline gap-2" data-testid={`pricing-chiro-${idx}`}>
                <span className="text-[#0F5156] font-medium">{item.service}</span>
                <span className="text-[#0F5156]/50 text-sm">({item.duration})</span>
                <span className="flex-1 border-b-2 border-dotted border-[#0F5156]/20 mx-2"></span>
                <span className="text-[#0F5156] font-semibold text-lg">{item.price}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Massages */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Heart className="w-6 h-6 text-[#0F5156]" />
            <h3 className="text-2xl font-medium text-[#0F5156]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Massages à domicile
            </h3>
          </div>
          <div className="space-y-4">
            {pricing.massages.map((item, idx) => (
              <div key={idx} className="flex items-baseline gap-2" data-testid={`pricing-massage-${idx}`}>
                <span className="text-[#0F5156] font-medium">{item.service}</span>
                <span className="text-[#0F5156]/50 text-sm">({item.duration})</span>
                <span className="flex-1 border-b-2 border-dotted border-[#0F5156]/20 mx-2"></span>
                <span className="text-[#0F5156] font-semibold text-lg">{item.price}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[#0F5156]/60 text-sm">
            * Massages disponibles les mardis, jeudis et vendredis
          </p>
          <p className="mt-4 text-[#0F5156] font-medium text-sm">
            Paiement accepté par carte et espèces
          </p>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="apropos" className="py-24 md:py-32 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#E6F2F2] rounded-[3rem] transform -rotate-3"></div>
              <img
                src={ASSETS.cabinetImage}
                alt="Cabinet de chiropraxie Marie Millot"
                className="relative rounded-[2.5rem] w-full h-[400px] lg:h-[500px] object-cover shadow-[0_20px_60px_rgba(15,81,86,0.15)]"
                data-testid="about-image"
              />
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <p className="text-sm tracking-widest uppercase text-[#0F5156]/60 font-semibold mb-4">
              Qui suis-je ?
            </p>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Marie Millot
            </h2>
            <div className="space-y-6 text-[#0F5156]/70 leading-relaxed text-justify">
              <p>
                Très jeune, les métiers du soin manuel m'ont attirée.
              </p>
              <p>
                Je suis convaincue par l'approche holistique et plurifactorielle de la chiropraxie : elle permet de comprendre le corps dans sa globalité et de traiter la cause plutôt que les symptômes.
              </p>
              <p>
                Après 6 années d'études à l'Institut Franco-Européen de Chiropraxie, j'ai débuté ma pratique en 2022. Collaborations et remplacements m'ont permis de développer mon expertise avant d'ouvrir un cabinet à Montreuil en août 2023.
              </p>
              <p>
                En 2025, j'ai enrichi ma palette de soins avec des formations en massages californien et suédois, chez Azenday. Mon objectif : vous offrir des moments de détente profonds et un retour à soi, si précieux dans notre quotidien souvent stressant.
              </p>
              <p className="text-[#0F5156] font-medium italic">
                Je vous accueille avec douceur et bienveillance, pour vous accompagner sur le chemin du mieux-être.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("Message envoyé !", {
          description: response.data.message
        });
        setFormData({ name: "", email: "", phone: "", message: "" });
      }
    } catch (error) {
      toast.error("Erreur", {
        description: "Une erreur est survenue. Veuillez réessayer ou me contacter directement par téléphone."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#F8FCFC]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-sm tracking-widest uppercase text-[#0F5156]/60 font-semibold mb-4">
            Parlons ensemble
          </p>
          <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Contact
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info & Map */}
          <div>
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E6F2F2] rounded-full flex items-center justify-center text-[#0F5156] flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-[#0F5156] mb-1">Téléphone</p>
                  <a href={`tel:${CONTACT.phone}`} className="text-[#0F5156]/70 hover:text-[#0F5156] transition-colors" data-testid="contact-phone">
                    {CONTACT.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E6F2F2] rounded-full flex items-center justify-center text-[#0F5156] flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-[#0F5156] mb-1">Email</p>
                  <a href={`mailto:${CONTACT.email}`} className="text-[#0F5156]/70 hover:text-[#0F5156] transition-colors" data-testid="contact-email">
                    {CONTACT.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E6F2F2] rounded-full flex items-center justify-center text-[#0F5156] flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-[#0F5156] mb-1">Adresse du cabinet</p>
                  <p className="text-[#0F5156]/70" data-testid="contact-address">
                    {CONTACT.address}
                  </p>
                  <p className="text-[#0F5156]/50 text-sm mt-1 italic">
                    {CONTACT.addressNote}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E6F2F2] rounded-full flex items-center justify-center text-[#0F5156] flex-shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-[#0F5156] mb-1">Massages à domicile</p>
                  <p className="text-[#0F5156]/70">
                    Mardi, Jeudi, Vendredi
                  </p>
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div className="map-container rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="google-map">
              <iframe
                title="Localisation du cabinet"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2625.1234567890123!2d2.4350000000000001!3d48.8600000000000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66d5e5a5a5a5a%3A0x5a5a5a5a5a5a5a5a!2s27%20Rue%20Colmet%20L%C3%A9pinay%2C%2093100%20Montreuil!5e0!3m2!1sfr!2sfr!4v1234567890123"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="border-0 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-medium text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Envoyez-moi un message
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-[#0F5156] mb-2 block">Nom *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                      className="rounded-xl border-[#E6F2F2] focus:border-[#0F5156] focus:ring-[#0F5156]"
                      data-testid="contact-form-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#0F5156] mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre@email.com"
                      className="rounded-xl border-[#E6F2F2] focus:border-[#0F5156] focus:ring-[#0F5156]"
                      data-testid="contact-form-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#0F5156] mb-2 block">Téléphone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06 XX XX XX XX"
                      className="rounded-xl border-[#E6F2F2] focus:border-[#0F5156] focus:ring-[#0F5156]"
                      data-testid="contact-form-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-[#0F5156] mb-2 block">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Décrivez-moi votre besoin..."
                      rows={5}
                      className="rounded-xl border-[#E6F2F2] focus:border-[#0F5156] focus:ring-[#0F5156] resize-none"
                      data-testid="contact-form-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#0F5156] hover:bg-[#0d4246] text-white rounded-full py-6 text-lg"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer-bg py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img 
              src={ASSETS.logo} 
              alt="Le corps en main" 
              className="h-12 w-auto bg-white rounded-lg p-1"
            />
            <div className="text-white">
              <p className="font-medium">Le corps en main</p>
              <p className="text-white/70 text-sm">Marie Millot - Chiropraxie & Massage</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <a 
              href={`tel:${CONTACT.phone}`}
              className="text-white/70 hover:text-white transition-colors flex items-center gap-2"
              data-testid="footer-phone"
            >
              <Phone className="w-4 h-4" />
              {CONTACT.phone}
            </a>
            <a 
              href={`mailto:${CONTACT.email}`}
              className="text-white/70 hover:text-white transition-colors flex items-center gap-2"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" />
              Contact
            </a>
          </div>
        </div>
        
        <div className="border-t border-white/20 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} Le corps en main - Marie Millot. Tous droits réservés.
          </p>
          <Link 
            to="/mentions-legales" 
            className="text-white/50 hover:text-white text-sm transition-colors flex items-center gap-1"
            data-testid="footer-legal-link"
          >
            <Shield className="w-3 h-3" />
            Mentions légales & RGPD
          </Link>
        </div>
      </div>
    </footer>
  );
};

// Legal Page
const LegalPage = () => {
  return (
    <div className="min-h-screen bg-[#F8FCFC]">
      {/* Simple Header */}
      <header className="bg-white border-b border-[#E6F2F2] py-4">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="flex items-center gap-3 text-[#0F5156] hover:text-[#0F5156]/70 transition-colors" data-testid="legal-back-link">
            <ArrowLeft className="w-5 h-5" />
            <img src={ASSETS.logo} alt="Le corps en main" className="h-10 w-auto" />
            <span className="font-medium">Retour au site</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-light text-[#0F5156] mb-12" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="legal-title">
          Mentions légales & RGPD
        </h1>

        {/* Mentions Légales */}
        <section className="mb-12" data-testid="legal-section">
          <h2 className="text-2xl font-medium text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            1. Mentions légales
          </h2>
          
          <div className="space-y-6 text-[#0F5156]/70 leading-relaxed">
            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Éditeur du site</h3>
              <p>Marie Millot - Chiropracteur</p>
              <p>27 rue Colmet Lepinay, 93100 Montreuil</p>
              <p>Téléphone : 07 56 92 66 24</p>
              <p>Email : marie.millot75@gmail.com</p>
            </div>

            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Activité</h3>
              <p>Chiropracteur - Profession de santé réglementée</p>
              <p>Diplôme : Institut Franco-Européen de Chiropraxie (IFEC)</p>
              <p>N° ADELI : 930007885</p>
              <p>N° RPPS : 10010142734</p>
            </div>

            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Hébergement</h3>
              <p>Ce site est hébergé par Emergent Labs</p>
            </div>
          </div>
        </section>

        {/* RGPD */}
        <section className="mb-12" data-testid="rgpd-section">
          <h2 className="text-2xl font-medium text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            2. Protection des données personnelles (RGPD)
          </h2>
          
          <div className="space-y-6 text-[#0F5156]/70 leading-relaxed">
            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Responsable du traitement</h3>
              <p>Marie Millot est responsable du traitement des données personnelles collectées sur ce site.</p>
            </div>

            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Données collectées</h3>
              <p>Les données collectées via le formulaire de contact sont :</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Nom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone (optionnel)</li>
                <li>Message</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Finalité du traitement</h3>
              <p>Ces données sont utilisées uniquement pour :</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Répondre à vos demandes de renseignements</li>
                <li>Vous recontacter concernant une prise de rendez-vous</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Durée de conservation</h3>
              <p>Vos données sont conservées pendant une durée maximale de 3 ans à compter du dernier contact, conformément aux recommandations de la CNIL.</p>
            </div>

            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Vos droits</h3>
              <p>Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Droit d'accès</strong> : obtenir la confirmation que vos données sont traitées et en recevoir une copie</li>
                <li><strong>Droit de rectification</strong> : demander la correction de données inexactes</li>
                <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données</li>
                <li><strong>Droit à la limitation</strong> : demander la limitation du traitement</li>
                <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
              </ul>
              <p className="mt-4">Pour exercer ces droits, contactez-moi par email à <a href="mailto:marie.millot75@gmail.com" className="text-[#0F5156] underline">marie.millot75@gmail.com</a></p>
            </div>

            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Sécurité des données</h3>
              <p>Des mesures techniques et organisationnelles appropriées sont mises en œuvre pour protéger vos données contre tout accès non autorisé, modification, divulgation ou destruction.</p>
            </div>

            <div>
              <h3 className="font-semibold text-[#0F5156] mb-2">Réclamation</h3>
              <p>Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la CNIL :</p>
              <p className="mt-2">Commission Nationale de l'Informatique et des Libertés (CNIL)</p>
              <p>3 Place de Fontenoy - TSA 80715 - 75334 Paris Cedex 07</p>
              <p>Site : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#0F5156] underline">www.cnil.fr</a></p>
            </div>
          </div>
        </section>

        {/* Cookies */}
        <section className="mb-12" data-testid="cookies-section">
          <h2 className="text-2xl font-medium text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            3. Cookies
          </h2>
          
          <div className="space-y-4 text-[#0F5156]/70 leading-relaxed">
            <p>Ce site n'utilise pas de cookies de traçage publicitaire ou analytique.</p>
            <p>Seuls des cookies techniques strictement nécessaires au fonctionnement du site peuvent être utilisés.</p>
          </div>
        </section>

        {/* Propriété intellectuelle */}
        <section data-testid="ip-section">
          <h2 className="text-2xl font-medium text-[#0F5156] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            4. Propriété intellectuelle
          </h2>
          
          <div className="space-y-4 text-[#0F5156]/70 leading-relaxed">
            <p>L'ensemble du contenu de ce site (textes, images, logo) est la propriété exclusive de Marie Millot ou fait l'objet d'une autorisation d'utilisation.</p>
            <p>Toute reproduction, représentation ou diffusion, totale ou partielle, du contenu de ce site est interdite sans autorisation préalable.</p>
          </div>
        </section>

        {/* Date de mise à jour */}
        <div className="mt-16 pt-8 border-t border-[#E6F2F2]">
          <p className="text-[#0F5156]/50 text-sm">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-[#0F5156] py-6">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/70 text-sm">
            © {new Date().getFullYear()} Le corps en main - Marie Millot
          </p>
        </div>
      </footer>
    </div>
  );
};

// Home Page Component

// Home Page Component
const HomePage = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <Navigation isOpen={menuOpen} setIsOpen={setMenuOpen} />
      <main>
        <HeroSection />
        <ServicesSection />
        <QuoteSection />
        <PricingSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/mentions-legales" element={<LegalPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
